import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { fetchUnitUser, setUnitUser, deleteUnit, setUnitMotionSuppression } from "actions/Units";
import { fetchUserUnits } from "actions/Users";
import { useModal } from "context/ModalContext";
import { returnDateFromTimestamp } from "utilities";
import TabbedSection from "components/TabbedSection";
import UnitDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitDetailModal";
import UnitSettingsModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitSettings/UnitSettingsModal";
import UserDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UserDetailModal";
import SharedUserDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/SharedUserDetailModal";
import ZonesDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/ZonesDetailModal";
import DoorAlarmDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/DoorAlarmDetailModal";
import Tag from "components/Tag";
import LoadingIndicator from "components/LoadingIndicator";
import ButtonIconOnly from "components/ButtonIconOnly";
import Popout from "components/Popout";
import ButtonContained from "components/ButtonContained";
import Icon from "components/Icon";
import Tooltip from "components/Tooltip";

const UnitsUsersDetailModal = ({ unitUser, trigger }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { openModal, updateModalTitle, updateModalSubTitle } = useModal();
  const motionSuppressionIconRef = useRef(null);
  const data = useSelector(state => state.units.unitUser);
  const site = useSelector(state => state.auth.site);
  const authUser = useSelector(state => state.auth.authUser);
  const userTimezone = useSelector(state => state.app.userTimezone);

  useEffect(() => {
    dispatch(setUnitUser(null));
    const payload = {};
    if (unitUser.id) {
      payload.unitId = unitUser.id;
      payload.unitUUID = unitUser.uuid;
    }
    if (unitUser.User?.id) {
      payload.userId = unitUser.User.id;
      payload.userUUID = unitUser.User.uuid;
      dispatch(fetchUserUnits(unitUser.User.uuid));
    }
    dispatch(fetchUnitUser(
      payload,
      {
        unregisteredUser: intl.formatMessage({ id: "comp.button.unregistered.user" })
      }
    ));
  }, [trigger]);

  const getSubtitle = (item) => {
    if (!item.id || item.accessType === "service" || item.accessType === "employee") {
      return (
        <p className="tw-paragraph-lg text-[var(--color-gray-600)]">
          {item.User?.role?.name}
        </p>
      );
    }

    let auctionTag = null;
    let rentalTag = null;
    let alarmTag = null;

    if (item.rentalState === "auction-smartentry") {
      if (item.auctionType === "unlock") {
        const unlocks = item.auctionUnlock;
        auctionTag = (
          <Tag
            text={`Auction - ${unlocks} unlock${unlocks > 1 ? "s" : ""} remaining`}
            color="red"
          />
        );
      } else {
        const expiration = returnDateFromTimestamp(
          item.auctionExpiration,
          authUser,
          site,
          userTimezone
        );

        const userName =
          item.User?.firstName && item.User?.lastName
            ? `- ${item.User.firstName} ${item.User.lastName}`
            : "";

        auctionTag = (
          <Tag
            text={`Auction - ${expiration} ${userName}`}
            color="red"
          />
        );
      }
    } else {
      rentalTag = (
        <Tag
          text={item.rentalStateLabel}
          color={item.rentalStateColor}
        />
      );
    }

    const isInAlarmState = item.inputDevices?.some(device => device.inputDeviceState === 'Alarm');

    if (isInAlarmState) {
      alarmTag = (
        <Tag
          text="Alarm"
          color="red"
        />
      );
    }

    return (
      <div className="flex gap-2">
        {auctionTag}
        {rentalTag}
        {alarmTag}
      </div>
    );
  };

  useEffect(() => {
    if (!data) {
      updateModalTitle(null);
      updateModalSubTitle(null);
      return;
    }

    const title = data.unit?.id && data.unit?.accessType !== "service" && data.unit?.accessType !== "employee" ? (
      <div className="flex gap-2 items-center">
        <h4 className="tw-heading-4 text-[var(--color-gray-800)]">{data.unit.name}</h4>
        {(authUser?.permissions?.includes("manage_locks") || authUser?.permissions?.includes("support_rekey")) && <ButtonIconOnly
          id="Unit-Detail-Modal-Settings-Icon"
          dataCy="Unit-Detail-Modal-Settings-Icon"
          icon="settings"
          type="default"
          onClick={() => {
            openModal(
              <UnitSettingsModal uuDetail={data} />,
              <UnitSettingsModalHeader unitUser={unitUser} data={data} />
            );
          }}
        />}
        {(Number(data.unit.motionNotificationCount) >= Number(site.motionNotificationDailyThreshold)) && <div>
          <button
            type="button"
            ref={motionSuppressionIconRef}
            onClick={() => dispatch(setUnitMotionSuppression(
              {
                unitId: data.unit.id,
                suppressionEnabled: data.unit.motionNotificationsSuppressed === true ? false : true
              },
              {
                successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
                failMessage: intl.formatMessage({ id: "notifications.error.failed" }),
                origin: "uuDetailModal",
                unregisteredUser: intl.formatMessage({ id: "comp.button.unregistered.user" }),
                unitUser: {
                  unitId: unitUser.id,
                  unitUUID: unitUser.uuid,
                  userId: unitUser.User?.id,
                  userUUID: unitUser.User?.uuid
                }
              }
            ))}
          >
            <Icon
              name={data.unit.motionNotificationsSuppressed === true ? "bellOff" : "bell"}
              size={24}
              className="text-[var(--color-gray-400)]"
            />
          </button>
          <Tooltip
            anchorRef={motionSuppressionIconRef}
            text={data.unit.motionNotificationsSuppressed === true ? intl.formatMessage({ id: "tooltip.units.motionSuppressed" }) : intl.formatMessage({ id: "tooltip.units.motionNotSuppressed" })}
          />
        </div>}
      </div>
    ) : data.unit.User?.fullName;

    // const title = data.unit?.id && data.unit?.accessType !== "service" ? data.unit.name : data.unit.User?.fullName;
    const subTitle = getSubtitle(data.unit);
    updateModalTitle(title);
    updateModalSubTitle(subTitle);
  }, [data, data?.unit?.motionNotificationsSuppressed, data?.unit?.motionNotificationCount]);

  const getTabs = () => {
    const tabs = [];
    if (data?.unit?.id && data?.unit?.accessType !== "service" && data.unit?.accessType !== "employee") tabs.push({ tab: "Unit", label: intl.formatMessage({ id: "pages.units.Type" }), showLabel: true });
    if (data?.unit?.User?.id) tabs.push({ tab: "User", label: intl.formatMessage({ id: "pages.users.Type" }), showLabel: true });
    if (data?.sharedUsers?.length) tabs.push({ tab: "Shared Users", label: intl.formatMessage({ id: "pages.users.shared" }), showLabel: true });
    if (data?.unit?.accessType === "service") tabs.push({ tab: "Zones", label: intl.formatMessage({ id: "pages.zones.Types" }), showLabel: true });
    if (data?.unit?.inputDevices?.length > 0) tabs.push({ tab: "Door Alarm", label: intl.formatMessage({ id: "pages.inputModule.inputType.alarm" }), showLabel: true });

    return tabs;
  };

  const tabs = getTabs();

  const tabComponents = {
    Unit: <UnitDetailModal uuDetail={data} unitUser={unitUser} />,
    User: <UserDetailModal uuDetail={data} unitUser={unitUser} />,
    "Shared Users": <SharedUserDetailModal uuDetail={data} />,
    Zones: <ZonesDetailModal uuDetail={data} />,
    "Door Alarm": <DoorAlarmDetailModal uuDetail={data} />,
  };

  return (
    <div>
      {
        !data ?
          <div className="flex justify-center">
            <LoadingIndicator />
          </div> :
          data?.unit?.rentalState === "available" && (!data?.unit?.inputDevices || data?.unit?.inputDevices?.length === 0)?
            <UnitDetailModal uuDetail={data} unitUser={unitUser} /> :
            !data?.unit.id ?
              <UserDetailModal uuDetail={data} unitUser={unitUser} /> :
              <TabbedSection topPadding={false} tabs={tabs} defaultTab={data?.unit?.id && data?.unit?.accessType !== "service" && data.unit?.accessType !== "employee" ? "Unit" : "User"}>
                {tabComponents}
              </TabbedSection>
      }
    </div>
  )
}

// Separate header component with its own state management
const UnitSettingsModalHeader = ({ unitUser, data }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { openModal, closeModal } = useModal();
  const popoutAnchorRef = useRef(null);

  const authUser = useSelector(state => state.auth.authUser);
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);

  return (
    <div className="flex justify-between items-center mr-2">
      <Popout
        isOpen={isPopoutOpen}
        onClose={() => setIsPopoutOpen(false)}
        anchorRef={popoutAnchorRef}
        positioning="bottom-left"
        header={false}>
        <div className="max-w-[300px] gap-3 flex flex-col pt-6">
          <p className="tw-paragraph-lg">{intl.formatMessage({ id: "comp.form.delete.description" })}</p>
          <div className="flex gap-2 justify-start">
            <ButtonContained
              id="Unit-Settings-Modal-Confirm-Delete-Button"
              dataCy="Unit-Settings-Modal-Confirm-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.delete" })}
              type="default"
              onClick={() => {
                dispatch(
                  deleteUnit(
                    unitUser.uuid,
                    {
                      successMessage: intl.formatMessage({ id: "notifications.success.delete" }),
                      failMessage: intl.formatMessage({ id: "notifications.error.fail.noDelete" }),
                      unitUUID: unitUser.uuid
                    }
                  )
                );
                setIsPopoutOpen(false);
                closeModal();
              }}
            />
            <ButtonContained
              id="Unit-Settings-Modal-Cancel-Delete-Button"
              dataCy="Unit-Settings-Modal-Cancel-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.cancel" })}
              type="defaultOutline"
              onClick={() => setIsPopoutOpen(false)}
            />
          </div>
        </div>
      </Popout>

      <div className="flex gap-2 items-center">
        <ButtonIconOnly
          id="Unit-Settings-Modal-Back-Button"
          dataCy="Unit-Settings-Modal-Back-Button"
          icon="arrowLeft"
          type="default"
          onClick={() => openModal(
            <UnitsUsersDetailModal unitUser={unitUser} />
          )}
        />
        <h4 className="tw-heading-4 text-[var(--color-gray-800)]">{`${data.unit.name} - ${intl.formatMessage({ id: "pages.settings.Types" })}`}</h4>
      </div>
      {data?.unit?.rentalState === "available" && authUser?.permissions?.includes("delete_units") && <ButtonIconOnly
        ref={popoutAnchorRef}
        id="Lock-Settings-Modal-Delete-Lock-Button"
        dataCy="Lock-Settings-Modal-Delete-Lock-Button"
        icon="trash"
        type="alt"
        size={20}
        customColor="text-[var(--color-alert)]"
        onClick={() => setIsPopoutOpen(true)}
      />}
    </div>
  );
};

export default UnitsUsersDetailModal;